import React, { useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import AddNewIcon from 'assets/icons/add-new'
import SpeciesParameter from './SpeciesParameter'
import { requestDeleteSiteSpecieAssesmentParameter } from 'api/siteSpecieAssesmentParameter'
import { requestUpdateSiteSpecie } from 'api/siteSpecie'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import Textarea from 'components/Textarea'
import MultiSelectionDropdown from 'components/MultiSelectDropdown'
import { SiteContext } from 'providers/site'
import { delay } from 'utils/delay'
import Checkbox from 'components/Checkbox'
import {
  AssessmentPageSubTittle,
  AssessmentPageSubTittleIconWrapper,
  AssessmentPageSubTittleWrapper,
  AssessmentPageTittle,
  AssessmentPageTittleWrapper
} from '../../../../style'
import {
  SpeciesDetailsAddParameter,
  SpeciesDetailsContainer
} from '../style'
import { congregatoryGroupOptions } from 'constants/parametersOptions'

const IrreversibleIndex = 7
const ReversibleIndex = 8
const qualifyIndex = 0
const reasonDefaultOptions = [
  { label: 'Qualifies', value: 1, disabled: false },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Taxonomic revision', value: 4, disabled: false },
  { label: 'Revision to Red List status', value: 5, disabled: false },
  { label: 'Revision of global threshold', value: 6, disabled: false },
  { label: 'No longer geographically restricted', value: 7, disabled: false },
  { label: 'Genuine decline, irreversible', value: 8, disabled: false },
  { label: 'Genuine decline, reversible', value: 9, disabled: false },
  { label: 'Genuine decline, other factors', value: 10, disabled: false }
]

const reasonQualifyOptions = [
  { label: 'Qualifies', value: 1, disabled: false },
  { label: 'Improved site information', value: 2, disabled: true },
  { label: 'KBA criteria revision', value: 3, disabled: true },
  { label: 'Taxonomic revision', value: 4, disabled: true },
  { label: 'Revision to Red List status', value: 5, disabled: true },
  { label: 'Revision of global threshold', value: 6, disabled: true },
  { label: 'No longer geographically restricted', value: 7, disabled: true },
  { label: 'Genuine decline, irreversible', value: 8, disabled: true },
  { label: 'Genuine decline, reversible', value: 9, disabled: true },
  { label: 'Genuine decline, other factors', value: 10, disabled: true }
]

const reasonNonQualifyOptions = [
  { label: 'Qualifies', value: 1, disabled: true },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Taxonomic revision', value: 4, disabled: false },
  { label: 'Revision to Red List status', value: 5, disabled: false },
  { label: 'Revision of global threshold', value: 6, disabled: false },
  { label: 'No longer geographically restricted', value: 7, disabled: false },
  { label: 'Genuine decline, irreversible', value: 8, disabled: false },
  { label: 'Genuine decline, reversible', value: 9, disabled: false },
  { label: 'Genuine decline, other factors', value: 10, disabled: false }
]

const reasonOptionsIrreversibleSelected = [
  { label: 'Qualifies', value: 1, disabled: true },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Taxonomic revision', value: 4, disabled: false },
  { label: 'Revision to Red List status', value: 5, disabled: false },
  { label: 'Revision of global threshold', value: 6, disabled: false },
  { label: 'No longer geographically restricted', value: 7, disabled: false },
  { label: 'Genuine decline, irreversible', value: 8, disabled: false },
  { label: 'Genuine decline, reversible', value: 9, disabled: true },
  { label: 'Genuine decline, other factors', value: 10, disabled: false }
]

const reasonOptionsReversibleSelected = [
  { label: 'Qualifies', value: 1, disabled: true },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Taxonomic revision', value: 4, disabled: false },
  { label: 'Revision to Red List status', value: 5, disabled: false },
  { label: 'Revision of global threshold', value: 6, disabled: false },
  { label: 'No longer geographically restricted', value: 7, disabled: false },
  { label: 'Genuine decline, irreversible', value: 8, disabled: true },
  { label: 'Genuine decline, reversible', value: 9, disabled: false },
  { label: 'Genuine decline, other factors', value: 10, disabled: false }
]

const tooltipXOffset = 0
const tooltipYOffset = 16
const maximumParameterListLength = 24
const inputSaveDelayTime = 300
const updateSensitiveSourceTimeOut = delay(inputSaveDelayTime)

function getSensitiveAtSiteFromItem (item) {
  return Object.hasOwn(item, 'sensitive_at_site')
    ? item.sensitive_at_site
    : item.sensitiveAtSite
}

function getSensitiveAtSiteSourceFromItem (item) {
  return Object.hasOwn(item, 'sensitive_at_site_source')
    ? item.sensitive_at_site_source
    : item.sensitiveAtSiteSource
}

const SpeciesDetailsAssessmentForm = ({
  item,
  getSiteSpecie,
  setItem,
  checkFields,
  site,
  setSiteProposalFieldValidation,
  isEditable
}) => {
  const params = useParams()
  const checkedValue = useRef([])
  const [parameterList, setParameterList] = useState(
    item.SpeciesAssesmentParameters.length ? item.SpeciesAssesmentParameters : [{ id: 'default' }]
  )
  const [qualificationReason, setQualificationReason] = useState(item.triggerReasonId || [])
  const [reassessmentRational, setReassessmentRational] = useState(item.triggerReasonText ? item.triggerReasonText : '')  
  const [checkedOptions, setCheckedOptions] = useState(reasonDefaultOptions)
  const [congregatoryGroup, setCongregatoryGroup] = useState(congregatoryGroupOptions.find((element) => {
    return element.value === item.CongregationGroupId
  }) || null)
  // Initialize state - handle both new assessments and reassessments
  // API returns snake_case: sensitive_at_site, sensitive_at_site_source (from DB)
  const [sensitiveAtSite, setSensitiveAtSite] = useState(() => {
    const dbValue = getSensitiveAtSiteFromItem(item)
    return Boolean(dbValue)
  })
  const [sensitiveAtSiteSource, setSensitiveAtSiteSource] = useState(() => {
    const isSensitive = getSensitiveAtSiteFromItem(item)
    if (isSensitive) {
      const raw = getSensitiveAtSiteSourceFromItem(item)
      return raw || ''
    }
    return ''
  })
  const [sensitiveAtSiteSourceError, setSensitiveAtSiteSourceError] = useState('')
  const { updateTriggerReasonId, siteProposalFieldValidation } = useContext(SiteContext)

  const qualified = item.lastReassessmentDate
    ? Boolean(item.triggerReasonId?.includes(1))
    : true

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID)

 async function handleChangeQualificationReason(e) {
   try {
     setQualificationReason(e)

     // If the species is disqualified (triggerReasonID > 1), skip capturing the criteria
     if (e > 1) {

       // Update only the disqualification reason without criteria
       await requestUpdateSiteSpecie(item.id, params.siteHistoryId, item.historyId, {
         triggerReasonId: e,
         reassessDateStatus: true,
       })

       return // Exit early to prevent any further processing
     }

     // If the species is not disqualified, proceed with updating the criteria
     await requestUpdateSiteSpecie(item.id, params.siteHistoryId, item.historyId, {
       triggerReasonId: e,
       criteria: item.criteria,
       reassessDateStatus: true,
     })

   } catch {
     // Trigger reason update failed
   }
 }

  
  function handleChangeValueChecked(Ids) {
    setCheckedOptions(Ids)
    checkedValue.current = Ids
    const isAnyNonQualifyOptionsSelected = Ids.some((item) => {
      return item.checked && item.value !== 1
    })

    if (Ids[IrreversibleIndex].checked) {
      return setCheckedOptions(reasonOptionsIrreversibleSelected)
    }

    if (Ids[ReversibleIndex].checked) {
      return setCheckedOptions(reasonOptionsReversibleSelected)
    }

    if (Ids[qualifyIndex].checked) {
      return setCheckedOptions(reasonQualifyOptions)
    }

    if (isAnyNonQualifyOptionsSelected) {
      return setCheckedOptions(reasonNonQualifyOptions)
    }

    setCheckedOptions(reasonDefaultOptions)
  }

  function handleChangeReassessmentRational(e) {
    setReassessmentRational(e.target.value)

    requestUpdateSiteSpecie(item.id, params.siteHistoryId, item.historyId, { triggerReasonText: e.target.value, reassessDateStatus: true, })
  }

  function addParameter() {
    setParameterList([...parameterList, { id: `parameter${Math.random()}`, isMaturePresent: false }])
  }

  function removeParameter(id) {
    setParameterList(
      parameterList.filter((element) => {
        return element.id !== id
      })
    )

    requestDeleteSiteSpecieAssesmentParameter(id, params.siteHistoryId, item.id, item.historyId).then(() => {
      getSiteSpecie()
    })
  }

  function updateParameterList(updtItem) {
    setParameterList((s) => {
      const tmpCopy = [...s]
      const found = s.findIndex((element) => {
        return element.id === updtItem.id
      })
      tmpCopy[found] = { ...updtItem }
      return tmpCopy
    })
  }

  const handleChangeCongregatoryGroup = (e) => {
    setCongregatoryGroup(e)
    requestUpdateSiteSpecie(item.id, params.siteHistoryId, item.historyId, { CongregationGroupId: e.value, reassessDateStatus: true, })
  }

  const renderParametersList = parameterList.sort((a, b) => {
    if(a.createdAt > b.createdAt) {
      return 1
    } else if (a.createdAt < b.createdAt){
      return -1
    }
    return 0
  }).map((parameter) => {
    return (
      <SpeciesParameter
        key={parameter.id}
        getSiteSpecie={getSiteSpecie}
        speciesId={item.id}
        removeOnCLik={removeParameter}
        parameter={parameter}
        updateParameterList={updateParameterList}
        setItem={setItem}
        checkParameterField={checkFields.numberOfAssessmentParameters}
        species={item}
        site={site}
        qualified={qualified}
        setSiteProposalFieldValidation={setSiteProposalFieldValidation}
        congregatoryGroup={congregatoryGroup}
        handleChangeCongregatoryGroup={handleChangeCongregatoryGroup}
      />
    )
  })

  useEffect(() => {
    if (item.SpeciesAssesmentParameters.length) {
      setParameterList(item.SpeciesAssesmentParameters)
    } else {
      setParameterList([{ id: 'default' }])
    }
  }, [item, setParameterList])
  

  useEffect(() => {
    const newTriggerReasonId = qualificationReason
    updateTriggerReasonId(newTriggerReasonId);
  }, [qualificationReason, updateTriggerReasonId]);

  useEffect(() => {
    setReassessmentRational(item.triggerReasonText ? item.triggerReasonText : '')
  }, [item.triggerReasonText])

  useEffect(() => {
    setQualificationReason(item.triggerReasonId || [])
  }, [item.triggerReasonId])

  useEffect(() => {
    const newCongregatoryGroup = congregatoryGroupOptions.find((element) => {
      return element.value === item.CongregationGroupId
    }) || null
    setCongregatoryGroup(newCongregatoryGroup)
  }, [item.CongregationGroupId])

  useEffect(() => {
    const dbValue = getSensitiveAtSiteFromItem(item)
    setSensitiveAtSite(Boolean(dbValue))
  }, [item.sensitive_at_site, item.sensitiveAtSite])

  useEffect(() => {
    setSensitiveAtSiteSourceError('')
    const isSensitive = getSensitiveAtSiteFromItem(item)
    if (isSensitive) {
      const sourceValue = getSensitiveAtSiteSourceFromItem(item)
      setSensitiveAtSiteSource(sourceValue || '')
    } else {
      setSensitiveAtSiteSource('')
    }
  }, [item.sensitive_at_site_source, item.sensitiveAtSiteSource, item.sensitive_at_site, item.sensitiveAtSite])

  function shouldValidateField(fieldName) {
    if (qualificationReason?.length === 0) {
      return checkFields[fieldName]
    }
    return true
  }

  const hasValidationRun = Boolean(siteProposalFieldValidation?.updated)
  const trimmedSensitiveSource = sensitiveAtSiteSource?.trim()
  const isSensitiveSourceMissing = sensitiveAtSite && (trimmedSensitiveSource === undefined || trimmedSensitiveSource === '')
  const derivedSensitiveSourceError = sensitiveAtSiteSourceError || (
    hasValidationRun && isSensitiveSourceMissing
      ? 'sensitiveAtSiteSourceRequired'
      : ''
  )

  const handleToggleSensitiveAtSite = () => {
    if (disabled) {
      return
    }
    const newValue = !sensitiveAtSite
    setSensitiveAtSite(newValue)
    setSensitiveAtSiteSourceError('')

    if (newValue) {
      // The actual requirement will be enforced on propose via backend validation.
      // We still persist the toggle state with the current (possibly empty) source value.
      const trimmedSource = sensitiveAtSiteSource ? sensitiveAtSiteSource.trim() : null
      requestUpdateSiteSpecie(
        item.id,
        params.siteHistoryId,
        item.historyId,
        {
          sensitiveAtSite: newValue,
          sensitiveAtSiteSource: trimmedSource,
          reassessDateStatus: true
        }
      ).then(() => {
        getSiteSpecie()
      }).catch((error) => {
        if (error && Array.isArray(error.failedFields) && error.failedFields.includes('sensitive_at_site_source')) {
          setSensitiveAtSiteSourceError(error.message || 'sensitiveAtSiteSourceRequired')
          return
        }
        setSensitiveAtSite(false)
        getSiteSpecie()
      })
      return
    }

    // When toggling off, clear source locally and on the backend
    setSensitiveAtSiteSource('')
    requestUpdateSiteSpecie(
      item.id,
      params.siteHistoryId,
      item.historyId,
      {
        sensitiveAtSite: newValue,
        sensitiveAtSiteSource: null,
        reassessDateStatus: true
      }
    ).then(() => getSiteSpecie()).catch((error) => {
      getSiteSpecie()
    })
  }

  const handleChangeSensitiveAtSiteSource = (e) => {
    const value = e.target.value
    setSensitiveAtSiteSource(value)
    setSensitiveAtSiteSourceError('')

    // Debounce backend updates so typing feels smooth and consistent with other
    // autosaving text fields (which also use a short delay before saving).
    const trimmedValue = value ? value.trim() : null

    updateSensitiveSourceTimeOut(() => {
      requestUpdateSiteSpecie(
        item.id,
        params.siteHistoryId,
        item.historyId,
        {
          sensitiveAtSite,
          sensitiveAtSiteSource: trimmedValue,
          reassessDateStatus: true
        }
      ).catch((error) => {
        if (error && Array.isArray(error.failedFields) && error.failedFields.includes('sensitive_at_site_source')) {
          setSensitiveAtSiteSourceError(error.message || 'sensitiveAtSiteSourceRequired')
        }
      })
    })
  }

  const showReassessmentSection = item.lastReassessmentDate !== null && site.reassessmentFlag

  return (
    <>
      <Container>
        <Row>
          <Col md={8} mdOffset={2}>
            <AssessmentPageTittleWrapper marginBottom={showReassessmentSection ? '64px' : '0px'}>
              <AssessmentPageTittle data-test='legend'>
                <FormattedMessage id='assessment' />
              </AssessmentPageTittle>
              <AssessmentPageSubTittleWrapper>
                <AssessmentPageSubTittle>
                  <FormattedMessage id='speciesAssessmentSubTittle' />
                </AssessmentPageSubTittle>
                <Tooltip
                  style={{ zIndex: 1000 }}
                  zIndex='1070'
                  placement='bottom'
                  trigger={['hover']}
                  align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                  overlay={{ label: 'assessment', infoTooltip: 'speciesAssessmentSubTittleHint' }}
                >
                  <AssessmentPageSubTittleIconWrapper>
                    <InfoIcon />
                  </AssessmentPageSubTittleIconWrapper>
                </Tooltip>
              </AssessmentPageSubTittleWrapper>
            </AssessmentPageTittleWrapper>
          </Col>
        </Row>
      </Container>
      {renderParametersList}
      {parameterList.length < maximumParameterListLength && (
        <SpeciesDetailsContainer>
          <SpeciesDetailsAddParameter onClick={addParameter} disabled={disabled}>
            <AddNewIcon />
            <FormattedMessage id='newParameter' />
          </SpeciesDetailsAddParameter>
        </SpeciesDetailsContainer>
      )}
      <SpeciesDetailsContainer>
        <div style={{ width: '100%' }}>
          <div style={{ marginBottom: '16px' }}>
            <Checkbox
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FormattedMessage id='sensitiveAtSiteLabel' />
                  <Tooltip
                    style={{ zIndex: 1000 }}
                    zIndex='1070'
                    placement='bottom'
                    trigger={['hover']}
                    align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                    overlay={{ label: 'sensitiveAtSiteLabel', infoTooltip: 'sensitiveAtSiteHint' }}
                  >
                    <AssessmentPageSubTittleIconWrapper>
                      <InfoIcon />
                    </AssessmentPageSubTittleIconWrapper>
                  </Tooltip>
                </span>
              }
              checked={Boolean(sensitiveAtSite)}
              onChange={handleToggleSensitiveAtSite}
              field='sensitiveAtSite'
              disabled={disabled}
            />
          </div>
          <Textarea
            value={sensitiveAtSiteSource}
            onChange={handleChangeSensitiveAtSiteSource}
            placeholder='sensitiveAtSiteSourcePlaceholder'
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FormattedMessage id='sensitiveAtSiteSource' />
                {sensitiveAtSite && <span style={{ color: '#d32f2f' }}>*</span>}
              </span>
            }
            infoTooltip='sensitiveAtSiteSourceHint'
            field='sensitiveAtSiteSource'
            height='128px'
            disabled={disabled || sensitiveAtSite !== true}
            zIndex='1070'
            /* Show error only after a validation run (e.g. after clicking Propose),
               when the species is marked sensitive at this site and the explanation
               is still empty. */
            error={
              sensitiveAtSite && derivedSensitiveSourceError
                ? { field: 'sensitiveAtSiteSource', error: derivedSensitiveSourceError }
                : { field: '', error: '' }
            }
          />
        </div>
      </SpeciesDetailsContainer>
      {showReassessmentSection && (
        <SpeciesDetailsContainer>
          <MultiSelectionDropdown
            value={qualificationReason}
            saveChanges={handleChangeQualificationReason}
            valueChecked={handleChangeValueChecked}
            label='triggerLabel'
            infoTooltip='triggerLabelHint'
            field='qualificationReasonSpecies'
            marginBottom='64px'
            menuWidth='416px'
            secondStepOptions={checkedOptions}
            placeholder='specieTriggerPlaceholder'
            fullWidthSelect={true}
            zIndex='1070'
            error={
              shouldValidateField('qualificationReasonSpecies')
                ? {
                    field: '',
                    error: ''
                  }
                : { field: 'qualificationReasonSpecies', error: 'pleaseCompleteThisField' }
            }
          />
        </SpeciesDetailsContainer>
      )}
      <SpeciesDetailsContainer>
        <div style={{ width: '100%' }}>
          {showReassessmentSection && (
            <Textarea
              value={reassessmentRational}
              onChange={handleChangeReassessmentRational}
              placeholder='justificationNoChangeSpeciePlaceholder'
              label='justificationNoChange'
              infoTooltip='qualificationReasonHint'
              field='rationalForReassessmentSpecies'
              height='128px'
              disabled={disabled}
              zIndex='1070'
            />
          )}
        </div>
        <div style={{ height: '150px', width: '100%' }} />
      </SpeciesDetailsContainer>
    </>
  )
}

const speciesItemPropTypes = {
  SpeciesAssesmentParameters: PropTypes.array,
  triggerReasonId: PropTypes.array,
  triggerReasonText: PropTypes.string,
  CongregationGroupId: PropTypes.number,
  sensitive_at_site: PropTypes.bool,
  sensitiveAtSite: PropTypes.bool,
  sensitive_at_site_source: PropTypes.string,
  sensitiveAtSiteSource: PropTypes.string,
  lastReassessmentDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.instanceOf(Date)]),
  id: PropTypes.number.isRequired,
  historyId: PropTypes.number.isRequired,
  criteria: PropTypes.array
}

SpeciesDetailsAssessmentForm.propTypes = {
  item: PropTypes.shape(speciesItemPropTypes).isRequired,
  getSiteSpecie: PropTypes.func.isRequired,
  setItem: PropTypes.func.isRequired,
  checkFields: PropTypes.shape({
    numberOfAssessmentParameters: PropTypes.bool,
    qualificationReasonSpecies: PropTypes.bool
  }).isRequired,
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  setSiteProposalFieldValidation: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired
}

export default SpeciesDetailsAssessmentForm
